import React, { forwardRef, useEffect, useState } from 'react';
import { startOfToday, sub, add, isWeekend, startOfDay } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css'; // Import DayPicker styles
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { fetchBankHolidays } from '@agria/utils';
import { FormField, FormFieldProps } from '../formField/FormField';
import { useResponsiveType } from '../../../utils';
import tokens from './tokens';

export interface InlineCalendarProps extends FormFieldProps {
  onChange: (date?: Date) => void;
  onBlur: () => void;
  label: string;
  invalid?: boolean;
  errorMessage?: string;
  value?: Date;
  previousDays?: number;

  advancedDays?: number;
  advancedDaysMin?: number;
  advancedDaysMax?: number;
  fromDateOverride?: Date;
  toDateOverride?: Date;
  preventBankHolidaysAsSelectable?: boolean;
  preventWeekendsAsSelectable?: boolean;
  allowAnyFutureDate?: boolean;
  allowAnyPastDate?: boolean;
}

export const StyledDayPicker = styled(DayPicker)`
  ${({ theme: { space, colors, borderRadii, maxMedia }, invalid }) => css`
    &.rdp {
      --rdp-cell-size: 70px;
      --rpd-cell-focus-size: 40px;
      --rpd-cell-focus-margin: calc(
        (var(--rdp-cell-size) - var(--rpd-cell-focus-size)) / 2
      );
      --rdp-caption-font-size: ${space[tokens.captionFontSize]};
      --rdp-accent-color: ${colors[tokens.accentColor]};
      --rdp-background-color: ${colors.utilityBackground};
      --rdp-accent-color-dark: ${colors[tokens.accentColor]};
      --rdp-background-color-dark: ${colors[tokens.accentColor]};
      --rdp-outline: 2px solid var(--rdp-accent-color);
      --rdp-outline-selected: 3px solid var(--rdp-accent-color);
      --rdp-selected-color: ${colors[tokens.selectedColor]};
      margin: 0;
    }

    .rdp-nav {
      display: flex;
      gap: ${space[tokens.navButtonSize]};
    }

    .rdp-nav_button {
      width: ${space[tokens.navButtonSize]};
      height: ${space[tokens.navButtonSize]};
    }

    .rdp-month {
      border: solid 1px
        ${invalid ? colors.systemError : colors[tokens.borderColor]};
      border-radius: ${borderRadii[tokens.borderRadius]};
      overflow: hidden;
    }

    .rdp-caption {
      background-color: ${colors[tokens.captionBackgroundColor]};
      padding: ${space.medium};
    }

    .rdp-caption_label {
      gap: ${space.xxxSmall};
      padding: 0 ${space.xSmall} 0 0;
    }

    .rdp-head_cell {
      text-transform: none;
      font-weight: 400;
      ${useResponsiveType('bodyMedium')};
      border-bottom: solid 1px ${colors[tokens.headCellBorderColor]};
    }

    .rdp-day {
      width: 32px;
      height: 32px;
      font-size: 16px;
      margin: 0 auto;
      font-weight: 900;
    }

    .rdp-button[disabled]:not(.rdp-day_selected) {
      color: ${colors[tokens.disabledColor]};
      opacity: 0.5;
      font-weight: 300;
    }

    .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
      background-color: var(--rdp-accent-color);
      color: ${colors[tokens.selectedColor]};
    }

    .rdp-button:focus-visible:not([disabled]) {
      background-color: var(--rdp-accent-color);
      color: ${colors[tokens.selectedColor]};
      border: none;
    }

    .rdp-button.rdp-day_selected:focus-visible:not([disabled]) {
      color: inherit;
      background-color: var(--rdp-accent-color);
      color: ${colors[tokens.selectedColor]};
    }

    .rdp-day_selected:focus-visible {
      outline: none;
    }

    @media (max-width: ${maxMedia.medium}) {
      &.rdp {
        --rdp-cell-size: 50px;
      }
    }

    @media (max-width: ${maxMedia.xSmall}) {
      &.rdp {
        --rdp-cell-size: 40px;
        --rdp-caption-font-size: 16px;
        font-size: 14px;
      }

      .rdp-caption {
        padding: ${space.small};
      }
    }
  `}
`;

export const InlineCalendar = forwardRef<HTMLDivElement, InlineCalendarProps>(
  (
    {
      label,
      hintText,
      invalid,
      onChange,
      onBlur,
      value,
      previousDays,
      advancedDays = 30,
      advancedDaysMin = 0,
      errorMessage,
      preventBankHolidaysAsSelectable = false,
      preventWeekendsAsSelectable = false,
      fromDateOverride,
      toDateOverride,
      allowAnyFutureDate,
      allowAnyPastDate,
      ...rest
    },
    ref
  ) => {
    const today = startOfToday();
    const [bankHolidays, setBankHolidays] = useState<Date[]>([]);

    useEffect(() => {
      if (preventBankHolidaysAsSelectable) {
        fetchBankHolidays().then(setBankHolidays);
      }
    }, [preventBankHolidaysAsSelectable]);

    let fromDate;
    if (allowAnyPastDate) {
      fromDate = undefined;
    } else if (previousDays && previousDays > 0) {
      fromDate = sub(today, { days: previousDays });
    } else {
      fromDate = add(today, { days: advancedDaysMin });
    }

    if (fromDateOverride) {
      fromDate = fromDateOverride;
    }

    let toDate;
    if (allowAnyFutureDate) {
      toDate = undefined;
    } else {
      toDate = add(today, { days: advancedDays });
    }

    if (toDateOverride) {
      toDate = toDateOverride;
    }

    const isDisabled = (date: Date) => {
      if (preventWeekendsAsSelectable && isWeekend(date)) return true;

      if (preventBankHolidaysAsSelectable) {
        return bankHolidays.some((holiday) => {
          const holidayDate = startOfDay(holiday).getTime();
          const selectedDate = startOfDay(date).getTime();
          return holidayDate === selectedDate;
        });
      }
      return false;
    };

    const disabledDays = [{ before: fromDate, after: toDate }, isDisabled];

    const handleDateChange = (selectedDate?: Date) => {
      onChange(selectedDate || undefined);
      onBlur();
    };

    return (
      <div ref={ref}>
        <FormField
          fieldAs="fieldset"
          label={label}
          hintText={hintText}
          largeLabelGap
          errorMessage={errorMessage}
          invalid={invalid}
          {...rest}
        >
          <StyledDayPicker
            mode="single"
            selected={value ? new Date(value) : undefined}
            onSelect={handleDateChange}
            fromDate={fromDate}
            toDate={toDate}
            disabled={disabledDays}
            invalid={invalid}
          />
        </FormField>
      </div>
    );
  }
);

InlineCalendar.displayName = 'InlineCalendar';

export default InlineCalendar;
